import { ActionIcon } from '@any-ui-react/core';
import { useMemo } from 'react';

import { useDeleteRecordModal } from '@/common/components/shared/delete-record-modal/useDeleteRecordModal';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { NoResult } from '@/common/components/shared/no-result/NoResult';
import { Table, type TableLayout } from '@/common/components/shared/table/Table';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import { DependentRelationLabels, type EmployeeDependentType } from '@/common/types';
import { DATETIME_FORMATS, DateUtils } from '@/common/utils';

import { DependentModal } from './DependentModal';

interface DependentTableProps {
  loading: boolean;
  items: EmployeeDependentType[];
  onRemove: (id: string) => void;
  onUpdate: (data: EmployeeDependentType) => void;
  showSelected?: boolean;
  isEditable?: boolean;
}

export const DependentTable = ({ items, loading, onUpdate, onRemove, isEditable }: DependentTableProps) => {
  const { openDeleteRecordModal } = useDeleteRecordModal();

  const { employeeDetail } = useEmployeeDetailContext();
  const isNewRecord = !employeeDetail.id;
  const { hasEmployeePersonalInformationDeleteAccess } = useEmployeeAccess();
  const isRemovable = hasEmployeePersonalInformationDeleteAccess || isNewRecord;

  const layout: Array<TableLayout<EmployeeDependentType>> = useMemo(
    () => [
      {
        headerRender: () => 'Name',
        accessor: 'name',
        cellRender: dependent =>
          isEditable ? (
            <DependentModal dependent={dependent} onSuccess={onUpdate}>
              <span className="text-blue-6 cursor-pointer">{dependent.name}</span>
            </DependentModal>
          ) : (
            dependent.name
          ),
      },
      {
        headerRender: () => 'Relation',
        accessor: 'relation',
        thClassName: '!max-w-[150px]',
        tdClassName: '!max-w-[150px]',
        cellRender: ({ relation }) => DependentRelationLabels[relation],
      },
      {
        headerRender: () => 'Date of Birth',
        accessor: 'dateOfBirth',
        thClassName: 'text-right !max-w-[140px]',
        tdClassName: 'text-right !max-w-[140px]',
        cellRender: ({ dateOfBirth }) => DateUtils.formatDate(dateOfBirth, DATETIME_FORMATS.MMMddyyyy),
      },
      {
        headerRender: () => 'Age',
        accessor: 'age',
        thClassName: 'text-right !max-w-[70px]',
        tdClassName: 'text-right !max-w-[70px]',
        cellRender: ({ age }) => `${age || 0}`,
      },
      ...(isRemovable
        ? [
            {
              headerRender: () => '',
              accessor: 'delete',
              thClassName: '!max-w-[72px]',
              tdClassName: '!max-w-[72px]',
              cellRender: ({ id }: EmployeeDependentType) => (
                <ActionIcon
                  className="ml-auto"
                  size="xs"
                  onClick={() => {
                    openDeleteRecordModal({
                      onSuccess: () => onRemove(id),
                    });
                  }}
                >
                  <MaterialIcons iconName="delete" className="cursor-pointer" />
                </ActionIcon>
              ),
            },
          ]
        : []),
    ],
    [isEditable, isRemovable, onRemove, onUpdate, openDeleteRecordModal],
  );

  return (
    <Table<EmployeeDependentType>
      data={items || []}
      emptyContainerClassName="border-gray-2 text-center"
      headerClassName="[&_th]:border-t-0"
      layout={layout}
      loading={loading}
      notFoundTemplate={
        <NoResult title="No dependents found" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
      }
      numOfStickyCols={0}
      wrapperClassName="border"
    />
  );
};
